import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

interface BloodBank {
  id: string;
  name: string;
  email: string;
  mobile: string;
  city: string;
  state: string;
  verification_status: string;
  subscription_plan: string;
  is_active: boolean;
  license_no: string;
  contact_person: string;
  address: string;
  area: string;
  pincode: string;
  adminSession?: boolean;
}

interface BloodBankAuthContextType {
  bloodBank: BloodBank | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminSession: boolean;
  bloodBankToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshBloodBank: () => Promise<void>;
}

const BloodBankAuthContext = createContext<BloodBankAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'blood_bank_auth';
const TOKEN_KEY = 'blood_bank_auth_token';

export const BloodBankAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [bloodBank, setBloodBank] = useState<BloodBank | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [bloodBankToken, setBloodBankToken] = useState<string | null>(null);

  // Initialize from localStorage — always verify server-side, no localStorage flag trust
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // adminSession flag is only trusted when set by server during impersonation login;
        // we still run server verification for both regular and admin sessions.
        verifyBloodBank(parsed.id).then((result) => {
          if (result.shouldAccept) {
            setBloodBank(parsed);
            // Only honour adminSession if it was stored by the server-side login flow
            setIsAdminSession(parsed.adminSession === true);
            const storedToken = localStorage.getItem(TOKEN_KEY);
            if (storedToken) setBloodBankToken(storedToken);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_KEY);
          }
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Graceful verification - returns shouldAccept: true if API fails or doesn't exist
  const verifyBloodBank = async (id: string): Promise<{ shouldAccept: boolean; isActive?: boolean }> => {
    try {
      const result = await bridgeApi.call<any>('blood_bank_verify', { id });
      // API responded successfully - check both is_active and verification_status (ENUM: pending/approved/rejected)
      if (result?.is_active === true && (result?.verification_status === 'approved' || result?.verification_status === 'verified')) {
        return { shouldAccept: true, isActive: true };
      }
      if (result?.is_active === false) {
        return { shouldAccept: false, isActive: false };
      }
      // Unknown response - accept session
      return { shouldAccept: true };
    } catch (error: any) {
      const msg: string = error?.message ?? '';
      // 404 = blood bank record doesn't exist in DB → stale session, clear it
      if (msg.includes('HTTP 404') || msg.includes('not found')) {
        return { shouldAccept: false };
      }
      // Any other error (network down, server error) → keep session (offline-first)
      return { shouldAccept: true };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    
    try {
      const result = await bridgeApi.post<any>('blood_bank_login', { email, password });
      
      if (!result.success) {
        setIsLoading(false);
        return { success: false, message: result.message || 'Login failed' };
      }

      const bank = result.blood_bank;

      // Check verification status
      if (bank.verification_status === 'pending') {
        setIsLoading(false);
        return { success: false, message: 'Your registration is pending admin approval. Please wait for verification.' };
      }

      if (bank.verification_status === 'rejected') {
        setIsLoading(false);
        return { success: false, message: `Registration was rejected. Reason: ${bank.rejection_reason || 'Contact support for details.'}` };
      }

      if (!bank.is_active) {
        setIsLoading(false);
        return { success: false, message: 'Your account is currently inactive. Please contact support.' };
      }

      // Success - store blood bank data
      const bloodBankData: BloodBank = {
        id: bank.id,
        name: bank.name,
        email: bank.email,
        mobile: bank.mobile,
        city: bank.city,
        state: bank.state,
        verification_status: bank.verification_status,
        subscription_plan: bank.subscription_plan || 'free',
        is_active: bank.is_active,
        license_no: bank.license_no,
        contact_person: bank.contact_person,
        address: bank.address,
        area: bank.area || '',
        pincode: bank.pincode,
      };

      setBloodBank(bloodBankData);
      setIsAdminSession(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bloodBankData));

      if (result.token) {
        setBloodBankToken(result.token);
        localStorage.setItem(TOKEN_KEY, result.token);
      }

      setIsLoading(false);

      return { success: true, message: 'Login successful!' };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, message: err.message || 'An error occurred. Please try again.' };
    }
  };

  const logout = () => {
    setBloodBank(null);
    setIsAdminSession(false);
    setBloodBankToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
  };

  const refreshBloodBank = async () => {
    if (!bloodBank) return;

    try {
      // NOTE: was 'blood_bank_get' — an action that doesn't exist on the
      // backend, so this call always silently failed. 'blood_bank_detail' is
      // the real self-service detail endpoint (see getBloodBankDetail()).
      const data = await bridgeApi.call<any>('blood_bank_detail', { id: bloodBank.id, token: bloodBankToken });
      let activePlan = data?.subscription_plan || 'free';

      try {
        const subData = await bridgeApi.call<any>('blood_bank_subscription', { blood_bank_id: bloodBank.id });
        if (subData?.status === 'active' && subData?.plan_name) {
          activePlan = subData.plan_name;
        }
      } catch {}

      if (data) {
        const updated: BloodBank = {
          id: data.id,
          name: data.name,
          email: data.email,
          mobile: data.mobile,
          city: data.city,
          state: data.state,
          verification_status: data.verification_status,
          subscription_plan: activePlan,
          is_active: data.is_active,
          license_no: data.license_no,
          contact_person: data.contact_person,
          address: data.address,
          area: data.area || '',
          pincode: data.pincode,
          adminSession: bloodBank.adminSession,
        };
        setBloodBank(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to refresh blood bank:', e);
    }
  };

  return (
    <BloodBankAuthContext.Provider
      value={{
        bloodBank,
        isLoading,
        isAuthenticated: !!bloodBank,
        isAdminSession,
        bloodBankToken,
        login,
        logout,
        refreshBloodBank,
      }}
    >
      {children}
    </BloodBankAuthContext.Provider>
  );
};

export const useBloodBankAuth = () => {
  const context = useContext(BloodBankAuthContext);
  if (context === undefined) {
    throw new Error('useBloodBankAuth must be used within a BloodBankAuthProvider');
  }
  return context;
};
